import React, { useState, Fragment, useEffect } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect, useSelector } from 'react-redux';
import Img from 'gatsby-image';

import { Grid, Divider, Col, Row } from '@components/Grid';
import styles from './styles.module.scss';
import BasketCounter from '@components/BasketCounter';
import { dashCase } from '@helpers/index';

const Navigation = ({ $navigation, $mobileNavigation }) => {
    const { user, basket } = useSelector(({ user, basket }) => ({ user, basket }));
    const [navigationMobileOpen, setNavigationMobileOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
    const [mobileNavigationClosing, setMobileNavigationClosing] = useState(false);

    const { logo, categories, products } = useStaticQuery(graphql`
        query Logo {
            logo: file(relativePath: { eq: "logo.png" }) {
                childImageSharp {
                    fluid(maxWidth: 129) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
            categories: allMerlinStoreCategory {
                nodes {
                    _id
                    name
                    merlinId
                }
            }
            products: allMerlinStoreProduct {
                nodes {
                    category {
                        _id
                    }
                }
            }
        }
    `);

    const [routes, setRoutes] = useState([
        {
            text: 'Home',
            path: '/',
            mobileOnly: true,
            partial: false,
        },
        {
            text: 'Products',
            path: '/shop/products',
            children: [
                {
                    text: 'All Products',
                    path: '/shop',
                    partial: false,
                },
                ...(categories?.nodes
                    ?.sort(({ merlinId }) => (merlinId === 'other' ? 1 : -1))
                    ?.filter(({ _id }) =>
                        products?.nodes?.some(({ category }) => category?._id === _id)
                    )
                    ?.map(category => ({
                        text: category?.name,
                        path: `/category/${dashCase(category?.name)}`,
                    })) ?? []),
            ],
            open: false,
        },
        {
            text: 'Dealers',
            path: '/dealers-locations',
        },
        {
            text: 'Videos',
            path: '/videos',
        },
        {
            text: 'Contact',
            path: '/contact',
        },
    ]);

    const renderRoutes = routes =>
        routes.map(
            ({
                text,
                path,
                mobileOnly = false,
                icon,
                children = [],
                disabled,
                partial = true,
                getProps,
                open,
            }) => (
                <li
                    key={`${text}_${path}`}
                    className={`${styles.navigationItem} ${
                        mobileOnly ? styles.navigationItemMobileOnly : ''
                    } ${!!children.length && open ? styles.navigationItemOpen : ''}`}
                >
                    {path && (!children || !children.length) ? (
                        <Link
                            className={`${styles.navigationItemLink} ${
                                disabled ? styles.navigationItemLinkDisabled : ''
                            } `}
                            activeClassName={styles.navigationItemLinkActive}
                            partiallyActive={partial}
                            to={path}
                            onClick={!children.length ? handleMobileClose : undefined}
                            getProps={getProps}
                        >
                            {!!icon && <FontAwesomeIcon icon={['fas', icon]} />}
                            <span>{text}</span>
                        </Link>
                    ) : (
                        <div
                            className={`${styles.navigationItemLink} ${
                                !disabled && !!children ? styles.navigationItemLinkHasChildren : ''
                            } ${disabled ? styles.navigationItemLinkDisabled : ''} ${
                                open ? styles.navigationItemLinkActive : ''
                            }`}
                            onClick={() =>
                                setRoutes(
                                    routes.map(route =>
                                        route.path === path
                                            ? { ...route, open: !route.open }
                                            : route
                                    )
                                )
                            }
                        >
                            <span className={styles.navigationItemLinkText}>{text}</span>
                            {!disabled && !!children && !!children.length && (
                                <span className={styles.navigationItemLinkArrow}>
                                    <FontAwesomeIcon icon={['fal', 'chevron-down']} />
                                </span>
                            )}
                        </div>
                    )}

                    {!disabled && !!children.length && (
                        <ul className={styles.dropdown}>{renderRoutes(children, true)}</ul>
                    )}
                </li>
            )
        );

    const handleMobileClose = () => {
        setMobileNavigationClosing(true);

        setTimeout(() => {
            setMobileNavigationOpen(false);
            setMobileNavigationClosing(false);
        }, 200);
    };

    const handleMobileToggle = () => {
        if (mobileNavigationOpen) {
            handleMobileClose();
            return;
        }

        setMobileNavigationOpen(true);
    };

    return (
        <Fragment>
            <div ref={$mobileNavigation} className={styles.navigationMobile}>
                <div
                    onKeyPress={handleMobileToggle}
                    tabIndex={0}
                    className={styles.navigationMobileToggle}
                    onClick={handleMobileToggle}
                >
                    <FontAwesomeIcon icon="bars" />
                </div>

                <Link
                    to="/"
                    className={styles.navigationMobileLogo}
                    aria-label="Logo"
                    onClick={() => setNavigationMobileOpen(!navigationMobileOpen)}
                >
                    {!!logo?.childImageSharp?.fluid && (
                        <Img
                            imgStyle={{ objectFit: 'contain' }}
                            fluid={logo?.childImageSharp?.fluid}
                        />
                    )}
                </Link>

                <Link to="/cart" className={styles.navigationMobileCart}>
                    <FontAwesomeIcon icon={['fal', 'shopping-cart']} />
                    <BasketCounter />
                </Link>
            </div>

            <nav
                ref={$navigation}
                className={`${styles.navigation} ${
                    mobileNavigationOpen ? styles.navigationMobileOpen : ''
                } ${mobileNavigationClosing ? styles.navigationMobileClosing : ''}`}
            >
                <Link
                    to="/"
                    className={styles.navigationLogo}
                    aria-label="Logo"
                    onClick={() => setNavigationMobileOpen(!navigationMobileOpen)}
                >
                    {!!logo?.childImageSharp?.fluid && (
                        <Img
                            imgStyle={{ objectFit: 'contain' }}
                            fluid={logo?.childImageSharp?.fluid}
                        />
                    )}
                </Link>
                <div className={styles.closeMobileNavigation} onClick={handleMobileClose}>
                    <span>&#10006;</span>
                    <Divider xsMargin={3} />
                </div>

                <ul className={styles.navigationContent}>{renderRoutes(routes)}</ul>

                <div className={styles.navigationRight}>
                    <Link to="/search" className={styles.navigationSearch}>
                        <FontAwesomeIcon icon={['fal', 'search']} />
                    </Link>
                    <ul className={styles.navigationAccount}>
                        <li
                            className={`${styles.navigationAccountItem} ${
                                !!user
                                    ? styles.navigationAccountItemUser
                                    : styles.navigationAccountItemLogin
                            }`}
                        >
                            {!!user ? (
                                <Link
                                    className={styles.navigationAccountItemLink}
                                    to="/account/overview"
                                >
                                    <span className={styles.navigationAccountItemLinkText}>
                                        {user?.firstName}
                                    </span>
                                    <span className={styles.navigationAccountItemLinkAvatar}>
                                        <img
                                            alt={`${user?.firstName}'s Avatar`}
                                            src={user?.avatar}
                                        />
                                    </span>
                                </Link>
                            ) : (
                                <Link className={styles.navigationAccountItemLink} to="/login">
                                    <span className={styles.navigationAccountItemLinkText}>
                                        Login
                                    </span>
                                    <FontAwesomeIcon icon={['fal', 'user']} />
                                </Link>
                            )}
                        </li>
                        <li
                            className={`${styles.navigationAccountItem} ${styles.navigationAccountItemBasket}`}
                        >
                            <Link className={styles.navigationAccountItemLink} to="/cart">
                                <span className={styles.navigationAccountItemLinkText}>Cart</span>
                                <FontAwesomeIcon icon={['fal', 'shopping-cart']} />
                                <BasketCounter />
                            </Link>
                        </li>
                        <li
                            className={`${styles.navigationAccountItem} ${styles.navigationAccountItemSignup}`}
                        >
                            <Link className={styles.navigationAccountItemLink} to="/register">
                                <span className={styles.navigationAccountItemLinkText}>
                                    Sign up
                                </span>
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            <span
                onClick={handleMobileToggle}
                className={`${styles.navigationMobileOverlay} ${
                    mobileNavigationOpen ? styles.navigationMobileOverlayOpen : ''
                }
                    ${mobileNavigationClosing ? styles.navigationMobileOverlayClosing : ''}`}
            />
        </Fragment>
    );
};

export default Navigation;
